import * as React from 'react'
import { Navigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import Plot from 'react-plotly.js'

import { setBreadcrumbs } from '../store/breadcrumbs'
import { setToolbar } from '../store/toolbar'

import HomeIcon from '@mui/icons-material/HomeRounded'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import MetricsIcon from '@mui/icons-material/BarChart'

import {
    Card, CardContent, Typography, Grid,
    CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip
} from '@mui/material'

import { Icon as IconifyIcon } from '@iconify/react'

import { fetchMetrics } from '../api/fetchMetrics'

import './MetricsDashboard.scss'

const LIST_LABELS = { cases: 'Cases', variants: 'Variants', genes: 'Genes' }
const ACTION_LABELS = { login: 'Logins', grid_view: 'List Views', import_upload: 'File Imports', api_import: 'API Imports', case_view: 'Case Detail Views', variant_view: 'Variant Detail Views' }
const CHART_COLORS = ['#1976d2', '#9c27b0', '#2e7d32', '#f57c00', '#c62828', '#00838f']

function StatCard({ title, value, icon, color, subtitle }) {
    return (
        <Card className="metric-stat-card">
            <CardContent>
                <div className="stat-header">
                    <IconifyIcon icon={icon} className="stat-icon" style={{ color }} />
                    <Typography variant="subtitle2" className="stat-title">{title}</Typography>
                </div>
                <Typography variant="h3" className="stat-value">
                    {typeof value === 'number' ? value.toLocaleString('de-DE') : (value ?? '—')}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" className="stat-subtitle" color="textSecondary">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

function EmptyCard({ title }) {
    return (
        <Card className="metric-card">
            <CardContent>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <Typography variant="body2" color="textSecondary">No data for this period.</Typography>
            </CardContent>
        </Card>
    )
}

export default function MetricsDashboard() {
    const user = useSelector((state) => state.user)
    const dispatch = useDispatch()

    const [days, setDays] = React.useState(30)
    const [data, setData] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(null)
    const [expandedError, setExpandedError] = React.useState(null)

    const breadcrumbs = [
        { key: 'home',    label: 'Home',          path: '/home',           icon: HomeIcon },
        { key: 'admin',   label: 'Admin',          path: '/admin',          icon: AdminIcon },
        { key: 'metrics', label: 'Usage Metrics',  path: '/admin/metrics',  icon: MetricsIcon }
    ]

    React.useEffect(() => {
        dispatch(setBreadcrumbs(breadcrumbs))
        dispatch(setToolbar(null))
    }, [])

    React.useEffect(() => { load() }, [days])

    const load = async () => {
        setLoading(true)
        setError(null)
        try {
            setData(await fetchMetrics(days))
        } catch {
            setError('Metrics could not be loaded.')
        } finally {
            setLoading(false)
        }
    }

    if (!user || user.isSuperuser !== true) return <Navigate replace to="/home" />

    const pc = { responsive: true, displayModeBar: false }  // shared Plotly config

    // ── Engagement over time ───────────────────────────────────────────────────
    const renderEngagementChart = () => {
        const logins = data?.loginsByDay ?? []
        const dau    = data?.dauByDay    ?? []
        if (logins.length === 0 && dau.length === 0) return <EmptyCard title="Engagement Over Time" />

        // Merge dates from both series
        const dateSet = new Set([...logins.map(d => d.date), ...dau.map(d => d.date)])
        const dates = [...dateSet].sort()
        const loginMap = new Map(logins.map(d => [d.date, d.count]))
        const dauMap   = new Map(dau.map(d => [d.date, d.count]))

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Engagement Over Time</Typography>
                    <Plot
                        data={[
                            {
                                x: dates, y: dates.map(d => loginMap.get(d) ?? 0),
                                type: 'scatter', mode: 'lines+markers',
                                name: 'Logins',
                                marker: { color: '#1976d2' }, line: { color: '#1976d2' }
                            },
                            {
                                x: dates, y: dates.map(d => dauMap.get(d) ?? 0),
                                type: 'scatter', mode: 'lines+markers',
                                name: 'Daily Active Users',
                                marker: { color: '#2e7d32' }, line: { color: '#2e7d32', dash: 'dot' }
                            }
                        ]}
                        layout={{
                            height: 280,
                            margin: { l: 50, r: 20, t: 10, b: 50 },
                            xaxis: { title: 'Date', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Count', gridcolor: '#f0f0f0', rangemode: 'tozero' },
                            legend: { orientation: 'h', y: -0.28, x: 0.5, xanchor: 'center' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Hour-of-day distribution ───────────────────────────────────────────────
    const renderHourOfDay = () => {
        const hourData = data?.hourOfDay ?? []
        if (hourData.every(d => d.count === 0)) return <EmptyCard title="Activity by Hour of Day" />
        const peak = hourData.reduce((a, b) => b.count > a.count ? b : a, hourData[0])
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Activity by Hour of Day (UTC)</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Historical distribution across the selected period — not filtered to today
                    </Typography>
                    <Plot
                        data={[{
                            x: hourData.map(d => `${String(d.hour).padStart(2,'0')}:00`),
                            y: hourData.map(d => d.count),
                            type: 'bar',
                            marker: {
                                color: hourData.map(d =>
                                    d.hour === peak.hour ? '#f57c00' : '#1976d2'
                                )
                            }
                        }]}
                        layout={{
                            height: 260,
                            margin: { l: 50, r: 20, t: 10, b: 60 },
                            xaxis: { title: 'Hour', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Actions', gridcolor: '#f0f0f0' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Day-of-week distribution ───────────────────────────────────────────────
    const renderDayOfWeek = () => {
        const dowData = data?.dayOfWeek ?? []
        if (dowData.every(d => d.count === 0)) return <EmptyCard title="Activity by Day of Week" />
        const peak = dowData.reduce((a, b) => b.count > a.count ? b : a, dowData[0])
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Activity by Day of Week</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Historical distribution across the selected period — not filtered to the current week
                    </Typography>
                    <Plot
                        data={[{
                            x: dowData.map(d => d.day),
                            y: dowData.map(d => d.count),
                            type: 'bar',
                            marker: {
                                color: dowData.map(d =>
                                    d.day === peak.day ? '#f57c00' : '#9c27b0'
                                )
                            }
                        }]}
                        layout={{
                            height: 260,
                            margin: { l: 50, r: 20, t: 10, b: 50 },
                            xaxis: { gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Actions', gridcolor: '#f0f0f0' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Action & grid breakdown ────────────────────────────────────────────────
    const renderActionBreakdown = () => {
        const breakdown = data?.actionBreakdown ?? []
        if (breakdown.length === 0) return <EmptyCard title="Action Breakdown" />
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Action Breakdown</Typography>
                    <Plot
                        data={[{
                            labels: breakdown.map(d => ACTION_LABELS[d.action] ?? d.action),
                            values: breakdown.map(d => d.count),
                            type: 'pie',
                            hole: 0.45,
                            marker: { colors: CHART_COLORS },
                            textinfo: 'label+percent',
                            hovertemplate: '%{label}: %{value}<extra></extra>'
                        }]}
                        layout={{
                            height: 260,
                            margin: { l: 20, r: 20, t: 10, b: 20 },
                            showlegend: false,
                            paper_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    const renderListViewBreakdown = () => {
        const grid = data?.gridTypeBreakdown ?? []
        if (grid.length === 0) return <EmptyCard title="List Views by Section" />
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>List Views by Section</Typography>
                    <Plot
                        data={[{
                            x: grid.map(d => LIST_LABELS[d.resource] ?? d.resource),
                            y: grid.map(d => d.count),
                            type: 'bar',
                            marker: { color: CHART_COLORS }
                        }]}
                        layout={{
                            height: 260,
                            margin: { l: 50, r: 20, t: 10, b: 50 },
                            xaxis: { gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Views', gridcolor: '#f0f0f0' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Activity by lab ────────────────────────────────────────────────────────
    const renderActivityByLab = () => {
        const byLab = data?.activityByLab ?? []
        if (byLab.length === 0) return <EmptyCard title="Activity by Lab" />
        const reversed = [...byLab].reverse()
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Activity by Lab</Typography>
                    <Plot
                        data={[
                            {
                                x: reversed.map(d => d.logins),
                                y: reversed.map(d => d.labName),
                                type: 'bar', orientation: 'h', name: 'Logins',
                                marker: { color: '#1976d2' }
                            },
                            {
                                x: reversed.map(d => d.caseListViews),
                                y: reversed.map(d => d.labName),
                                type: 'bar', orientation: 'h', name: 'Case Views',
                                marker: { color: '#2e7d32' }
                            },
                            {
                                x: reversed.map(d => d.variantListViews),
                                y: reversed.map(d => d.labName),
                                type: 'bar', orientation: 'h', name: 'Variant Views',
                                marker: { color: '#9c27b0' }
                            },
                            {
                                x: reversed.map(d => d.fileImports),
                                y: reversed.map(d => d.labName),
                                type: 'bar', orientation: 'h', name: 'File Imports',
                                marker: { color: '#f57c00' }
                            },
                            {
                                x: reversed.map(d => d.apiImports),
                                y: reversed.map(d => d.labName),
                                type: 'bar', orientation: 'h', name: 'API Imports',
                                marker: { color: '#37474f' }
                            }
                        ]}
                        layout={{
                            barmode: 'stack',
                            height: Math.max(280, byLab.length * 55 + 80),
                            margin: { l: 140, r: 20, t: 10, b: 50 },
                            xaxis: { title: 'Actions', gridcolor: '#f0f0f0' },
                            yaxis: { automargin: true },
                            legend: { orientation: 'h', y: -0.15, x: 0.5, xanchor: 'center' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Most-viewed cases / variants tables ───────────────────────────────────
    const renderTopItems = (title, rows, idKey, idLabel) => (
        <Card className="metric-card">
            <CardContent>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                {rows.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No data for this period.</Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow className="table-header-row">
                                    <TableCell><b>#</b></TableCell>
                                    <TableCell><b>{idLabel}</b></TableCell>
                                    <TableCell align="right"><b>Views</b></TableCell>
                                    <TableCell align="right"><b>Unique Users</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                                {r[idKey]}
                                            </code>
                                        </TableCell>
                                        <TableCell align="right"><b>{r.views}</b></TableCell>
                                        <TableCell align="right">{r.uniqueUsers}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    )

    // ── Submission trends ──────────────────────────────────────────────────────
    const renderSubmissionTrends = () => {
        const trends = data?.submissionTrends ?? []
        if (trends.length === 0) return <EmptyCard title="Case Submissions Over Time" />
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Case Submissions Over Time</Typography>
                    <Typography variant="caption" color="textSecondary">Last 2 years — cases by finding date (Befunddatum), grouped monthly</Typography>
                    <Plot
                        data={[{
                            x: trends.map(d => d.month),
                            y: trends.map(d => d.cases),
                            type: 'bar',
                            marker: { color: '#1976d2' },
                            name: 'Cases'
                        }]}
                        layout={{
                            height: 280,
                            margin: { l: 50, r: 20, t: 30, b: 60 },
                            xaxis: { title: 'Month', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Cases', gridcolor: '#f0f0f0', rangemode: 'tozero' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Lab submission cadence heatmap ─────────────────────────────────────────
    const renderLabCadence = () => {
        const raw = data?.labSubmissionCadence ?? []
        if (raw.length === 0) return <EmptyCard title="Lab Submission Cadence" />

        const labs   = [...new Set(raw.map(d => d.labName))].sort()
        const months = [...new Set(raw.map(d => d.month))].sort()
        const lookup = new Map(raw.map(d => [`${d.labName}||${d.month}`, d.count]))
        const z      = labs.map(lab => months.map(month => lookup.get(`${lab}||${month}`) ?? 0))

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Lab Submission Cadence</Typography>
                    <Typography variant="caption" color="textSecondary">Cases per lab per month (last 2 years, by finding date)</Typography>
                    <Plot
                        data={[{
                            x: months, y: labs, z,
                            type: 'heatmap',
                            colorscale: [[0, '#2196f3'], [1, '#d32f2f']],
                            showscale: true,
                            hovertemplate: '%{y} — %{x}: %{z} cases<extra></extra>'
                        }]}
                        layout={{
                            height: Math.max(250, labs.length * 45 + 100),
                            margin: { l: 130, r: 60, t: 30, b: 80 },
                            xaxis: { title: 'Month' },
                            yaxis: { automargin: true },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Classification drift ───────────────────────────────────────────────────
    const renderClassificationDrift = () => {
        const raw = data?.classificationDrift ?? []
        if (raw.length === 0) return <EmptyCard title="Classification Distribution Over Time" />

        const CLASS_COLORS = {
            'pathogenic':             '#c62828',
            'likely pathogenic':      '#ef6c00',
            'unclear':                '#9c27b0',
            'uncertain significance': '#9c27b0',
            'likely benign':          '#2e7d32',
            'benign':                 '#1565c0',
            'candidate gene':         '#00838f'
        }
        const months  = [...new Set(raw.map(d => d.month))].sort()
        const classes = [...new Set(raw.map(d => d.class))]
        const lookup  = new Map(raw.map(d => [`${d.class}||${d.month}`, d.count]))

        const traces = classes.map(cls => ({
            x: months,
            y: months.map(m => lookup.get(`${cls}||${m}`) ?? 0),
            type: 'bar',
            name: cls.charAt(0).toUpperCase() + cls.slice(1),
            marker: { color: CLASS_COLORS[cls.toLowerCase()] ?? '#78909c' }
        }))

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Classification Distribution Over Time</Typography>
                    <Typography variant="caption" color="textSecondary">Last 2 years — variant count by ACMG class per month</Typography>
                    <Plot
                        data={traces}
                        layout={{
                            barmode: 'stack',
                            height: 300,
                            margin: { l: 50, r: 20, t: 30, b: 60 },
                            xaxis: { title: 'Month', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Variants', gridcolor: '#f0f0f0' },
                            legend: { orientation: 'h', y: -0.25, x: 0.5, xanchor: 'center' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Top genes ──────────────────────────────────────────────────────────────
    const renderTopGenes = () => {
        const genes = data?.topGenes ?? []
        if (genes.length === 0) return <EmptyCard title="Top Genes by Variant Count" />
        const rev = [...genes].reverse()
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Top Genes by Variant Count</Typography>
                    <Plot
                        data={[{
                            x: rev.map(g => g.count),
                            y: rev.map(g => g.gene),
                            type: 'bar', orientation: 'h',
                            marker: { color: '#00838f' }
                        }]}
                        layout={{
                            height: Math.max(280, genes.length * 28 + 80),
                            margin: { l: 100, r: 20, t: 10, b: 50 },
                            xaxis: { title: 'Variant occurrences', gridcolor: '#f0f0f0' },
                            yaxis: { automargin: true },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── User retention ─────────────────────────────────────────────────────────
    const renderUserRetention = () => {
        const ret = data?.userRetention ?? []
        if (ret.length === 0) return <EmptyCard title="New vs Returning Users" />
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>New vs Returning Users</Typography>
                    <Typography variant="caption" color="textSecondary">Daily unique logins — "new" means first login falls within the selected period</Typography>
                    <Plot
                        data={[
                            {
                                x: ret.map(d => d.date), y: ret.map(d => d.newUsers),
                                type: 'bar', name: 'New Users',
                                marker: { color: '#2e7d32' }
                            },
                            {
                                x: ret.map(d => d.date), y: ret.map(d => d.returningUsers),
                                type: 'bar', name: 'Returning Users',
                                marker: { color: '#1976d2' }
                            }
                        ]}
                        layout={{
                            barmode: 'stack',
                            height: 280,
                            margin: { l: 50, r: 20, t: 10, b: 60 },
                            xaxis: { title: 'Date', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Users', gridcolor: '#f0f0f0', rangemode: 'tozero' },
                            legend: { orientation: 'h', y: -0.28, x: 0.5, xanchor: 'center' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Top users table ────────────────────────────────────────────────────────
    const renderTopUsersTable = () => {
        const users = data?.topUsers ?? []
        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Top Users by Activity</Typography>
                    {users.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No user data for this period.</Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow className="table-header-row">
                                        <TableCell><b>User</b></TableCell>
                                        <TableCell><b>Lab</b></TableCell>
                                        <TableCell align="right"><b>Logins</b></TableCell>
                                        <TableCell align="right"><b>Case Views</b></TableCell>
                                        <TableCell align="right"><b>Variant Views</b></TableCell>
                                        <TableCell align="right"><b>Case Detail Views</b></TableCell>
                                        <TableCell align="right"><b>Variant Detail Views</b></TableCell>
                                        <TableCell align="right"><b>File Imports</b></TableCell>
                                        <TableCell align="right"><b>API Imports</b></TableCell>
                                        <TableCell align="right"><b>Total</b></TableCell>
                                        <TableCell><b>Last Seen</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((u, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell>{u.username}</TableCell>
                                            <TableCell>
                                                {u.labName
                                                    ? <Chip label={u.labName} size="small" variant="outlined" />
                                                    : '—'}
                                            </TableCell>
                                            <TableCell align="right">{u.logins}</TableCell>
                                            <TableCell align="right">{u.caseListViews ?? 0}</TableCell>
                                            <TableCell align="right">{u.variantListViews ?? 0}</TableCell>
                                            <TableCell align="right">{u.caseViews ?? 0}</TableCell>
                                            <TableCell align="right">{u.variantViews ?? 0}</TableCell>
                                            <TableCell align="right">{u.fileImports ?? 0}</TableCell>
                                            <TableCell align="right">{u.apiImports ?? 0}</TableCell>
                                            <TableCell align="right"><b>{u.actions}</b></TableCell>
                                            <TableCell>
                                                {u.lastSeen
                                                    ? new Date(u.lastSeen).toLocaleDateString('de-DE')
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        )
    }

    // ── Import completion states ────────────────────────────────────────────────
    const renderImportStates = () => {
        const states = data?.importStates ?? []
        if (states.length === 0) return <EmptyCard title="Import Completion" />

        const STATE_COLORS = {
            FINISHED: '#4caf50',
            ERROR:    '#f44336',
            CANCELED: '#ff9800',
            RUNNING:  '#2196f3',
            PENDING:  '#9e9e9e',
        }
        const STATE_LABELS = {
            FINISHED: 'Finished',
            ERROR:    'Error',
            CANCELED: 'Cancelled',
            RUNNING:  'Running',
            PENDING:  'Pending',
        }

        const total = states.reduce((s, d) => s + d.count, 0)
        const finished = states.find(d => d.state === 'FINISHED')?.count ?? 0
        const completionRate = total > 0 ? Math.round((finished / total) * 100) : 0

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Import Completion</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        All-time — {completionRate}% of imports finished successfully
                    </Typography>
                    <Plot
                        data={[{
                            values: states.map(d => d.count),
                            labels: states.map(d => STATE_LABELS[d.state] ?? d.state),
                            type: 'pie',
                            hole: 0.5,
                            marker: { colors: states.map(d => STATE_COLORS[d.state] ?? '#9e9e9e') },
                            textinfo: 'label+percent',
                            hovertemplate: '%{label}: %{value} (%{percent})<extra></extra>'
                        }]}
                        layout={{
                            height: 260,
                            margin: { l: 20, r: 20, t: 10, b: 10 },
                            showlegend: false,
                            paper_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Session depth ───────────────────────────────────────────────────────────
    const renderSessionDepth = () => {
        const sd = data?.sessionDepth
        if (!sd) return <EmptyCard title="Session Depth" />

        const dist = sd.dist ?? []
        const buckets = [
            { label: '1–3',   min: 1,  max: 3  },
            { label: '4–7',   min: 4,  max: 7  },
            { label: '8–15',  min: 8,  max: 15 },
            { label: '16–30', min: 16, max: 30 },
            { label: '30+',   min: 31, max: Infinity },
        ]
        const bucketCounts = buckets.map(b => dist.filter(v => v >= b.min && v <= b.max).length)

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Session Depth</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Actions per user per day — avg {sd.avg}, max {sd.max} across {sd.sessions} sessions
                    </Typography>
                    <Plot
                        data={[{
                            x: buckets.map(b => b.label),
                            y: bucketCounts,
                            type: 'bar',
                            marker: { color: '#26a69a' }
                        }]}
                        layout={{
                            height: 240,
                            margin: { l: 50, r: 20, t: 10, b: 50 },
                            xaxis: { title: 'Actions per session', gridcolor: '#f0f0f0' },
                            yaxis: { title: 'Sessions', gridcolor: '#f0f0f0' },
                            paper_bgcolor: 'white', plot_bgcolor: 'white'
                        }}
                        config={pc} style={{ width: '100%' }}
                    />
                </CardContent>
            </Card>
        )
    }

    // ── Top search terms ────────────────────────────────────────────────────────
    const renderTopSearchTerms = () => {
        const terms = data?.topSearchTerms ?? []
        if (terms.length === 0) return <EmptyCard title="Top Search Terms" />

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Top Search Terms</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Most searched queries across all grids in the selected period
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow className="table-header-row">
                                    <TableCell><b>Grid</b></TableCell>
                                    <TableCell><b>Search term</b></TableCell>
                                    <TableCell align="right"><b>Searches</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {terms.map(({ term, count }) => {
                                    const [grid, ...rest] = (term ?? '').split(':')
                                    const query = rest.join(':')
                                    return (
                                        <TableRow key={term} hover>
                                            <TableCell sx={{ color: 'text.secondary', width: 80 }}>{grid}</TableCell>
                                            <TableCell>{query}</TableCell>
                                            <TableCell align="right">{count}</TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        )
    }

    // ── Import error breakdown ──────────────────────────────────────────────────
    const renderImportErrors = () => {
        const errors = data?.importErrors ?? []
        if (errors.length === 0) return <EmptyCard title="Import Error Breakdown" />

        return (
            <Card className="metric-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Import Error Breakdown</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        All-time — most common reasons imports failed
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow className="table-header-row">
                                    <TableCell><b>Error</b></TableCell>
                                    <TableCell align="right"><b>Files</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {errors.map(({ error, count, imports }) => (
                                    <React.Fragment key={error}>
                                        <TableRow
                                            hover
                                            onClick={() => setExpandedError(expandedError === error ? null : error)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                <div style={expandedError === error ? { wordBreak: 'break-word' } : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                                                    {error}
                                                </div>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip label={count} size="small" variant="outlined" />
                                            </TableCell>
                                        </TableRow>
                                        {expandedError === error && (
                                            <TableRow>
                                                <TableCell colSpan={2} sx={{ py: 0, bgcolor: '#fafafa' }}>
                                                    <div style={{ padding: '6px 0' }}>
                                                        {(imports ?? []).map(imp => (
                                                            <div key={imp.id} style={{ padding: '2px 0' }}>
                                                                <a
                                                                    href={`/admin/imports?importId=${imp.id}`}
                                                                    style={{ fontSize: '0.75rem', color: '#1976d2' }}
                                                                >
                                                                    {imp.name || imp.id}
                                                                </a>
                                                                {imp.created && (
                                                                    <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: 8 }}>
                                                                        {new Date(imp.created).toLocaleDateString('de-DE')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        )
    }

    const s = data?.summary ?? {}

    // Peak hour label
    const peakHour = (data?.hourOfDay ?? []).reduce(
        (best, d) => d.count > best.count ? d : best,
        { hour: null, count: 0 }
    )
    const peakHourLabel = peakHour.hour !== null
        ? `${String(peakHour.hour).padStart(2,'0')}:00–${String(peakHour.hour + 1).padStart(2,'0')}:00 UTC`
        : null

    // Peak day label
    const peakDay = (data?.dayOfWeek ?? []).reduce(
        (best, d) => d.count > best.count ? d : best,
        { day: null, count: 0 }
    )

    return (
        <div className="page-content metrics-dashboard">
            <h2>Usage Metrics</h2>

            <div className="period-selector">
                <Typography variant="body2" className="period-label">Time period:</Typography>
                <ToggleButtonGroup
                    value={days} exclusive size="small"
                    onChange={(_, val) => { if (val !== null) setDays(val) }}
                >
                    <ToggleButton value={7}>7 days</ToggleButton>
                    <ToggleButton value={30}>30 days</ToggleButton>
                    <ToggleButton value={90}>90 days</ToggleButton>
                </ToggleButtonGroup>
            </div>

            {loading && (
                <div className="loading-container">
                    <CircularProgress />
                    <p>Loading metrics...</p>
                </div>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && data && (
                <>
                    {/* ── Summary stat cards ── */}
                    <Grid container spacing={2} className="summary-cards">
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Total Logins"         value={s.totalLogins}             icon="mdi:login"                   color="#1976d2" subtitle={`of ${s.totalUsers} registered users`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Active Users"         value={s.uniqueActiveUsers}        icon="mdi:account-check"           color="#2e7d32" subtitle={`${s.inactiveUsers} inactive this period`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Avg Actions / User"   value={s.avgActionsPerUser}        icon="mdi:chart-timeline-variant"  color="#9c27b0" subtitle="among active users" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <StatCard title="File Imports" value={s.totalFileImports} icon="mdi:upload" color="#00838f" subtitle="Excel / CSV" />
                                </Grid>
                                <Grid item xs={6}>
                                    <StatCard title="API Imports"  value={s.totalApiImports}  icon="mdi:api"    color="#37474f" subtitle="via API" />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Case List Views"      value={s.totalCaseListViews}       icon="mdi:table-eye"               color="#f57c00" subtitle="cases list opened" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Variant List Views"   value={s.totalVariantListViews}    icon="mdi:table-search"            color="#00838f" subtitle="variants list opened" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Case Detail Views"    value={s.totalCaseViews}           icon="mdi:file-document-outline"   color="#6d4c41" subtitle="individual cases opened" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Variant Detail Views" value={s.totalVariantViews}        icon="mdi:dna"                     color="#c62828" subtitle="individual variants opened" />
                        </Grid>
                    </Grid>

                    {/* ── Engagement over time + action breakdown ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} lg={8}>{renderEngagementChart()}</Grid>
                        <Grid item xs={12} lg={4}>{renderActionBreakdown()}</Grid>
                    </Grid>

                    {/* ── Temporal patterns ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} md={8}>{renderHourOfDay()}</Grid>
                        <Grid item xs={12} md={4}>{renderDayOfWeek()}</Grid>
                    </Grid>

                    {/* Insight chips */}
                    {(peakHourLabel || peakDay.day) && (
                        <div className="insight-chips">
                            {peakHourLabel && (
                                <Chip
                                    icon={<IconifyIcon icon="mdi:clock-outline" />}
                                    label={`Peak hour: ${peakHourLabel}`}
                                    variant="outlined" className="insight-chip"
                                />
                            )}
                            {peakDay.day && (
                                <Chip
                                    icon={<IconifyIcon icon="mdi:calendar-week" />}
                                    label={`Busiest day: ${peakDay.day}`}
                                    variant="outlined" className="insight-chip"
                                />
                            )}
                        </div>
                    )}

                    {/* ── List views by section + activity by lab ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} md={4}>{renderListViewBreakdown()}</Grid>
                        <Grid item xs={12} md={8}>{renderActivityByLab()}</Grid>
                    </Grid>

                    {/* ── Most viewed cases & variants ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} md={6}>
                            {renderTopItems('Most Viewed Cases', data?.topCases ?? [], 'caseId', 'Case ID')}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {renderTopItems('Most Viewed Variants', data?.topVariants ?? [], 'variantId', 'Variant ID')}
                        </Grid>
                    </Grid>

                    {/* ── Top users table (full width) ── */}
                    <div className="charts-row">{renderTopUsersTable()}</div>

                    {/* ── New vs returning users + submission trends + import states ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} lg={5}>{renderUserRetention()}</Grid>
                        <Grid item xs={12} lg={4}>{renderSubmissionTrends()}</Grid>
                        <Grid item xs={12} lg={3}>{renderImportStates()}</Grid>
                    </Grid>

                    {/* ── Top genes + classification drift ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} md={5}>{renderTopGenes()}</Grid>
                        <Grid item xs={12} md={7}>{renderClassificationDrift()}</Grid>
                    </Grid>

                    {/* ── Session depth + search terms + import errors ── */}
                    <Grid container spacing={2} className="charts-row">
                        <Grid item xs={12} md={4}>{renderSessionDepth()}</Grid>
                        <Grid item xs={12} md={4}>{renderTopSearchTerms()}</Grid>
                        <Grid item xs={12} md={4}>{renderImportErrors()}</Grid>
                    </Grid>

                    {/* ── Lab submission cadence heatmap (full width) ── */}
                    <div className="charts-row">{renderLabCadence()}</div>
                </>
            )}
        </div>
    )
}
